import { Box, Card, Typography, useColorScheme } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import L from "leaflet";
import "leaflet-rastercoords";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef } from "react";
import MapLegend from "../assets/MapLegend.png";

interface MapPoint {
  x: number;
  y: number;
  id: string;
  name: string;
}

interface InteractiveMapProps {
  points?: MapPoint[];
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({ points }) => {
  const mapRef = useRef<null | HTMLDivElement>(null);
  const theme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";

  useEffect(() => {
    if (mapRef.current?.dataset.uninitialized) {
      delete mapRef.current.dataset.uninitialized;
      const map = L.map(mapRef.current, {
        crs: L.CRS.Simple,
        minZoom: 2,
        maxZoom: 7,
      });
      const img = { width: 9728, height: 9728 };
      const rc = new L.RasterCoords(map, [img.width, img.height]);
      map.setView(rc.unproject([img.width / 2 + 200, img.height / 2 - 400]), 2);
      map.attributionControl.setPrefix("");
      L.tileLayer("/map/tiles/{z}/{x}/{y}.png", {
        noWrap: true,
        attribution: "",
        bounds: rc.getMaxBounds(),
        maxNativeZoom: rc.zoomLevel(),
      }).addTo(map);
    }
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 4,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          position: "relative",
        }}
      >
        {/* Magical glow effect behind title */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "400px",
            height: "100px",
            background:
              "radial-gradient(ellipse, rgba(120, 119, 198, 0.15) 0%, transparent 70%)",
            filter: "blur(20px)",
            zIndex: -1,
          }}
        />

        <Typography
          level="h2"
          sx={{
            color: isDark ? "white" : "#1e293b",
            fontWeight: 300,
            letterSpacing: "0.05em",
            textShadow: isDark
              ? "0 0 20px rgba(120, 119, 198, 0.5)"
              : "0 0 10px rgba(120, 119, 198, 0.3)",
            mb: 2,
          }}
        >
          Map of Osgara, 499 F.O.
        </Typography>

        <Typography
          level="body-md"
          sx={{
            color: isDark
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(30, 41, 59, 0.7)",
            mb: 1,
          }}
        >
          Thank you Antonio Purrmann!
        </Typography>

        <Typography
          level="body-sm"
          sx={{
            color: isDark
              ? "rgba(255, 255, 255, 0.6)"
              : "rgba(30, 41, 59, 0.6)",
            fontStyle: "italic",
          }}
        >
          For demystifying how to get this map working in this tech stack! ❤️
        </Typography>

        {/* Decorative elements */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: "40px",
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
            }}
          />
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(120, 119, 198, 0.8) 0%, transparent 70%)",
              boxShadow: "0 0 8px rgba(120, 119, 198, 0.6)",
            }}
          />
          <Box
            sx={{
              width: "40px",
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
            }}
          />
        </Box>
      </Box>

      {/* Map Container */}
      <Card
        variant="outlined"
        sx={{
          width: "95%",
          maxWidth: "1200px",
          background: "rgba(0, 0, 0, 0.4)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
          overflow: "hidden",
          position: "relative",
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Background gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.05) 0%, transparent 50%)",
            zIndex: -1,
          }}
        />

        {/* Map Legend - Desktop only */}
        {!isMobile && (
          <Box
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              zIndex: 1000,
              background: "rgba(0, 0, 0, 0.8)",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
            }}
          >
            <img
              loading="lazy"
              src={MapLegend}
              style={{
                width: "250px",
                display: "block",
              }}
              alt="Map Legend"
            />
          </Box>
        )}

        {/* Map */}
        <Box
          sx={{
            p: 2,
          }}
        >
          <div
            ref={mapRef}
            style={{
              width: "100%",
              height: isMobile ? "500px" : "700px",
              background: "#DDCFC3",
              borderRadius: "12px",
              overflow: "hidden",
              border: "2px solid rgba(255, 255, 255, 0.1)",
            }}
            data-uninitialized
          />
        </Box>

        {/* Mobile Legend */}
        {isMobile && (
          <Box
            sx={{
              p: 2,
              pt: 0,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                background: "rgba(0, 0, 0, 0.6)",
                borderRadius: "12px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                overflow: "hidden",
                maxWidth: "300px",
              }}
            >
              <img
                loading="lazy"
                src={MapLegend}
                style={{
                  width: "100%",
                  display: "block",
                }}
                alt="Map Legend"
              />
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default InteractiveMap;
